import type { ReactNode } from "react";
import type { HelpChatMessage as HelpChatMessageType } from "../../ai/aiApi";

interface HelpChatMessageProps {
  message: HelpChatMessageType;
}

/** Aplica inline: **bold** */
function applyInline(raw: string): ReactNode {
  const parts = raw.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**")
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : part
      )}
    </>
  );
}

/** Renderiza Markdown simples linha a linha */
function renderMarkdown(text: string): ReactNode {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let listItems: ReactNode[] = [];
  let listKind: "ol" | "ul" | null = null;

  const flushList = (key: number) => {
    if (!listItems.length) return;
    if (listKind === "ol") {
      blocks.push(
        <div key={key} className="my-1 space-y-0.5">
          {listItems}
        </div>
      );
    } else {
      blocks.push(
        <div key={key} className="my-1 space-y-0.5">
          {listItems}
        </div>
      );
    }
    listItems = [];
    listKind = null;
  };

  lines.forEach((line, i) => {
    const numbered = line.match(/^\s*(\d+)\.\s+(.+)$/);
    const bullet = line.match(/^\s*[-*]\s+(.+)$/);

    if (numbered) {
      if (listKind === "ul") flushList(i);
      listKind = "ol";
      listItems.push(
        <div key={i} className="flex gap-1.5 leading-snug">
          <span className="shrink-0 font-medium">{numbered[1]}.</span>
          <span>{applyInline(numbered[2])}</span>
        </div>
      );
    } else if (bullet) {
      if (listKind === "ol") flushList(i);
      listKind = "ul";
      listItems.push(
        <div key={i} className="flex gap-1.5 leading-snug">
          <span className="shrink-0">•</span>
          <span>{applyInline(bullet[1])}</span>
        </div>
      );
    } else {
      flushList(i);
      if (line.trim()) {
        blocks.push(
          <p key={i} className="mb-1 last:mb-0">
            {applyInline(line)}
          </p>
        );
      }
    }
  });

  flushList(lines.length);
  return <>{blocks}</>;
}

export function HelpChatMessage({ message }: HelpChatMessageProps) {
  const isUser = message.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        style={{
          maxWidth: "85%",
          borderRadius: isUser
            ? "18px 4px 18px 18px"
            : "4px 18px 18px 18px",
          padding: "10px 14px",
          fontSize: "14px",
          lineHeight: "1.5",
          background: isUser ? "color-mix(in oklab, var(--primary) 15%, white)" : "#fff",
          color: isUser ? "var(--foreground)" : "var(--foreground)",
          border: isUser ? "1px solid color-mix(in oklab, var(--primary) 30%, transparent)" : "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        {isUser ? message.content : renderMarkdown(message.content)}
      </div>
    </div>
  );
}
